import React, { useEffect, useState, useContext } from 'react'
import { useAuth } from '@praxis/component-auth'
import { useAnalytics } from '@praxis/analytics/client'
import {
  Button,
  Card,
  Grid,
  Heading,
  Input,
  Layout,
  Tooltip,
  useToaster,
} from '@enterprise-ui/canvas-ui-react'
import { SectionHeader } from '../../globalComponents/SectionHeader'
import {
  createNotification,
  getNotifications,
  updateNotification,
} from '../../services/requests'
import { ManageNotificationsRecords } from './ManageNotificationsRecords'
import {
  DUPLICATE_NOTIFICATION,
  MANAGE_NOTIFICATION_EVENT_FIELDS,
  MANAGE_NOTIFICATION_TIMEZONE_FIELDS,
  TOASTER_DEFAULTS,
  CUSTOM_ANALYTICS_TYPES,
} from '../../globalConfig/constants'
import { NotificationCriteriaContext } from '../../globalState/ManageNotifications'
import { VendorContext } from '../../globalState/Vendor'
import { Autocomplete } from '@enterprise-ui/canvas-ui-react-autocomplete'
import EnterpriseIcon, { HelpIcon } from '@enterprise-ui/icons'
import _ from 'lodash'

export const ManageNotificationsPage = () => {
  const auth = useAuth()
  const userLanId = auth?.session?.userInfo?.lanId
  const makeToast = useToaster()
  const { sendEvent } = useAnalytics()
  const [vendorState] = React.useContext(VendorContext)
  const [currentNotifications, setCurrentNotifications] = useState([])
  const [waitingOnGetNotifications, setWaitingOnGetNotifications] =
    useState(false)
  const [waitingOnCreateNotification, setWaitingOnCreateNotification] =
    useState(false)
  const [waitingOnUpdateNotification, setWaitingOnUpdateNotification] =
    useState(false)
  const [isUpdatingNotification, setIsUpdatingNotification] = useState(false)
  const [updatingNotificationId, setUpdatingNotificationId] = useState(null)
  const constructInitialCheckBoxOptions = () => {
    const returnArray = []
    Object.values(MANAGE_NOTIFICATION_EVENT_FIELDS).forEach((field) => {
      returnArray.push({
        value: field.keyName,
        label: field.label,
        highlight: false,
        disabled: false,
        indeterminate: false,
      })
    })
    return returnArray
  }
  const constructInitialRadioOptions = () => {
    const returnArray = []
    Object.values(MANAGE_NOTIFICATION_TIMEZONE_FIELDS).forEach((field) => {
      returnArray.push({
        value: field.keyName,
        label: field.label,
        highlight: false,
        disabled: false,
        indeterminate: false,
      })
    })
    return returnArray
  }
  const [notificationCriteriaState, notificationCriteriaDispatch] = useContext(
    NotificationCriteriaContext,
  )
  const checkboxOptions = constructInitialCheckBoxOptions()
  const radioOptions = constructInitialRadioOptions()

  useEffect(() => {
    sendEvent({
      event: {
        type: CUSTOM_ANALYTICS_TYPES.PAGE_VIEW,
        id: 'Manage Notifications Page',
      },
      client: {
        employee_login_id: userLanId,
      },
    })
  }, [sendEvent, userLanId])

  useEffect(() => {
    fetchNotifications()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const fetchNotifications = () => {
    const sendFailureToast = () => {
      makeToast({
        ...TOASTER_DEFAULTS,
        type: 'error',
        heading: 'Error',
        message: 'Failed to get your notification.',
      })
    }

    setWaitingOnGetNotifications(true)
    getNotifications()
      .then((response) => {
        setWaitingOnGetNotifications(false)
        if (response?.status === 200) {
          setCurrentNotifications(response.data)
        } else {
          sendFailureToast()
        }
      })
      .catch(() => {
        setWaitingOnGetNotifications(false)
        sendFailureToast()
      })
  }

  const createRequestBody = () => {
    return {
      ...notificationCriteriaState.notificationEvents,
      vendorIds: [
        notificationCriteriaState.notificationCriteria?.vendorIds?.value,
      ],
      timezone: notificationCriteriaState.notificationCriteria?.['timezone'],
      brands: [],
      merchHierarchy: null,
    }
  }

  const handleCreateNotification = () => {
    setWaitingOnCreateNotification(true)
    const sendFailureToast = () => {
      makeToast({
        ...TOASTER_DEFAULTS,
        type: 'error',
        heading: 'Error',
        message: 'Failed to create notification.',
      })
    }
    const duplicateNotificationToast = () => {
      makeToast({
        ...TOASTER_DEFAULTS,
        type: 'error',
        heading: 'Error',
        message:
          'A notification with this criteria already exists, please update it instead.',
      })
    }

    createNotification(createRequestBody())
      .then((response) => {
        setWaitingOnCreateNotification(false)
        if (response?.status === 200) {
          if (response?.data) {
            fetchNotifications()
          }
          clearCriteria()
        } else {
          if (response?.data?.errorCode === DUPLICATE_NOTIFICATION) {
            duplicateNotificationToast()
          } else {
            sendFailureToast()
          }
        }
      })
      .catch((e) => {
        setWaitingOnCreateNotification(false)
        if (e?.response?.data?.errorCode === DUPLICATE_NOTIFICATION) {
          duplicateNotificationToast()
        } else {
          sendFailureToast()
        }
      })
  }

  const handleUpdateNotification = () => {
    setWaitingOnUpdateNotification(true)
    const sendSuccessToast = () => {
      makeToast({
        ...TOASTER_DEFAULTS,
        type: 'success',
        heading: 'Update Successful',
        message: 'Successfully updated notification.',
      })
    }
    const sendFailureToast = () => {
      makeToast({
        ...TOASTER_DEFAULTS,
        type: 'error',
        heading: 'Error',
        message: 'Failed to update notification.',
      })
    }

    updateNotification(updatingNotificationId, createRequestBody())
      .then((response) => {
        setWaitingOnUpdateNotification(false)
        if (response?.status === 200) {
          if (response?.data) {
            sendSuccessToast()
            updateCurrentNotifications(response.data)
          }
          clearCriteria()
          setIsUpdatingNotification(false)
          setUpdatingNotificationId(null)
        } else {
          sendFailureToast()
        }
      })
      .catch(() => {
        setWaitingOnUpdateNotification(false)
        sendFailureToast()
      })
  }

  const notificationCriteriaSetter = (inputValue) => {
    notificationCriteriaDispatch({
      type: 'update_notifications_criteria',
      notificationCriteria: {
        ...notificationCriteriaState.notificationCriteria,
        vendorIds: inputValue,
      },
    })
  }

  const eventSetter = (inputValue) => {
    notificationCriteriaDispatch({
      type: 'update_notification_events',
      notificationEvents: {
        ...notificationCriteriaState.notificationEvents,
        eventTypes: inputValue,
      },
    })
  }

  const timezoneSetter = (inputValue) => {
    notificationCriteriaDispatch({
      type: 'update_notifications_criteria',
      notificationCriteria: {
        ...notificationCriteriaState.notificationCriteria,
        timezone: inputValue,
      },
    })
  }

  const clearCriteria = () => {
    const clearedCriteria = {
      vendorIds: null,
      timezone: null,
    }

    notificationCriteriaDispatch({
      type: 'update_notifications_criteria',
      notificationCriteria: {
        ...notificationCriteriaState.notificationCriteria,
        ...clearedCriteria,
      },
    })
    notificationCriteriaDispatch({
      type: 'update_notification_events',
      notificationEvents: {
        ...notificationCriteriaState.notificationEvents,
        eventTypes: [],
      },
    })
  }

  const updateCurrentNotifications = (updatedNotifications) => {
    setCurrentNotifications((prevNotifications) => {
      if (Array.isArray(updatedNotifications)) {
        // Update all notifications in the list
        const updatedNotificationIds = updatedNotifications.map(
          (notification) => notification.notificationPreference.notificationId,
        )
        return prevNotifications.map((notification) =>
          updatedNotificationIds.includes(
            notification.notificationPreference.notificationId,
          )
            ? updatedNotifications.find(
                (updatedNotification) =>
                  updatedNotification.notificationPreference.notificationId ===
                  notification.notificationPreference.notificationId,
              )
            : notification,
        )
      } else {
        // Update a single notification
        return prevNotifications.map((notification) =>
          notification.notificationPreference.notificationId ===
          updatedNotifications.notificationPreference.notificationId
            ? updatedNotifications
            : notification,
        )
      }
    })
  }

  const isCreateButtonDisabled = () => {
    const hasVendors =
      notificationCriteriaState?.notificationCriteria?.vendorIds

    const hasEvents =
      notificationCriteriaState.notificationEvents.eventTypes.length !== 0

    const hasTimezone =
      notificationCriteriaState?.notificationCriteria?.timezone !== null

    return !(hasVendors && hasEvents && hasTimezone)
  }

  const setIsUpdating = (boolean, notificationId) => {
    setIsUpdatingNotification(boolean)
    setUpdatingNotificationId(notificationId)
  }

  const vendorOptions = Object.entries(vendorState?.vendors).map(
    ([key, value]) => {
      return {
        id: `${value} - ${key}`,
        label: `${value} - ${key}`,
        value: parseInt(key),
      }
    },
  )

  useEffect(() => {
    if (
      vendorOptions?.length &&
      notificationCriteriaState?.notificationCriteria?.vendorIds?.length
    ) {
      const newValues =
        notificationCriteriaState?.notificationCriteria?.vendorIds
      newValues.forEach((valueObj, index) => {
        vendorOptions.forEach((optionObj) => {
          if (optionObj.value === valueObj.value) {
            newValues[index] = optionObj
          }
        })
      })
      if (
        !_.isEqual(
          newValues,
          notificationCriteriaState?.notificationCriteria?.vendorIds,
        )
      ) {
        notificationCriteriaSetter(newValues)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notificationCriteriaState?.notificationCriteria?.vendorIds])

  const headerSubtext = (
    <>
      <span>
        {`This is where you can set and manage your notification preferences, 
        allowing you to subscribe to cost change events based on specific criteria. 
        These notifications will be delivered via email and will include a summary of all the events you’ve subscribed to. 
        If there are no updates for the events you’ve subscribed to, you will not receive a notification.`}
      </span>
    </>
  )

  const eventExplanation = (
    <>
      <span>
        <strong>
          <u>Pending Review Cost Changes:</u>
        </strong>
        <br />

        {`Cost changes that need to be approved or rejected by a Target employee.`}
      </span>
      <br />
      <br />
      <span>
        <strong>
          <u>Approved Costs:</u>
        </strong>
        <br />

        {`Cost changes that have been auto approved by the system or by a Target employee.`}
      </span>
      <br />
      <br />
      <span>
        <strong>
          <u>Rejected Costs:</u>
        </strong>
        <br />

        {`Cost changes that have been auto rejected by the system or by a Target employee.`}
      </span>
      <br />
      <br />
      <span>
        <strong>
          <u>Effective Costs:</u>
        </strong>
        <br />

        {`Cost changes that have been approved and are in effect starting today.`}
      </span>
    </>
  )

  return (
    <>
      <SectionHeader
        pageHeading="Manage Notifications"
        subtext={headerSubtext}
      />
      <Layout.Body data-testid="homePage" includeRail>
        <Card className="hc-pa-normal hesp-card-border hesp-card-background">
          <Grid.Container className="hc-pa-normal">
            <Grid.Item className="hesp-general-fullwidth hc-pa-none">
              <Heading size={5}>
                Notification Criteria <span className="hesp-alert-text">*</span>
              </Heading>{' '}
              <div style={{ columnGap: '24px' }} className="hc-pl-lg hc-pt-xs">
                <Autocomplete
                  className="hesp-notifications-scContainer"
                  id="vendor_autocomplete"
                  data-testid="vendor_autocomplete"
                  label="Vendors"
                  options={vendorOptions}
                  onUpdate={(id, value) => notificationCriteriaSetter(value)}
                  value={
                    notificationCriteriaState?.notificationCriteria?.vendorIds
                  }
                />
              </div>
            </Grid.Item>
          </Grid.Container>
          <Grid.Container className="hc-pa-normal">
            <Grid.Item className="hesp-general-fullwidth hc-pa-none">
              <Heading size={5}>
                Event Types
                <Tooltip content={eventExplanation} location="top-left">
                  <EnterpriseIcon
                    data-testid="timezone_help_icon"
                    style={{ paddingLeft: '5px' }}
                    icon={HelpIcon}
                    size="sm"
                  />
                </Tooltip>
                <span className="hesp-alert-text">*</span>
              </Heading>{' '}
              <Input.Checkboxes
                className="hesp-checkbox-label"
                id="event_checkbox"
                data-testid="event_checkbox"
                options={checkboxOptions}
                onUpdate={(id, value) => eventSetter(value)}
                value={
                  notificationCriteriaState?.notificationEvents?.eventTypes
                }
              />
            </Grid.Item>
          </Grid.Container>
          <Grid.Container className="hc-pa-normal">
            <Grid.Item className="hesp-general-fullwidth hc-pa-none">
              <Heading size={5}>
                Timezone
                <Tooltip
                  content="Notifications will be delivered on weekday mornings in your selected time zone. "
                  location="top-left"
                >
                  <EnterpriseIcon
                    data-testid="timezone_help_icon"
                    style={{ paddingLeft: '5px' }}
                    icon={HelpIcon}
                    size="sm"
                  />
                </Tooltip>
                <span className="hesp-alert-text">*</span>
              </Heading>
              <Input.Radio
                className="hesp-radio-label"
                id="timezone_radio"
                data-testid="timezone_radio"
                options={radioOptions}
                onUpdate={(id, value) => timezoneSetter(value)}
                value={
                  notificationCriteriaState?.notificationCriteria?.timezone
                }
              />
            </Grid.Item>
          </Grid.Container>
        </Card>
        <Grid.Container
          className="hc-pt-normal"
          direction="row-reverse"
          spacing="dense"
        >
          <Grid.Item>
            {isUpdatingNotification ? (
              <>
                <Button
                  type="secondary"
                  className="hc-mr-normal"
                  onClick={() => {
                    setIsUpdatingNotification(false)
                    setUpdatingNotificationId(null)
                    clearCriteria()
                  }}
                  data-testid="notifications-cancel-update-button"
                >
                  CANCEL
                </Button>
                <Button
                  type="primary"
                  onClick={() => handleUpdateNotification()}
                  disabled={isCreateButtonDisabled()}
                  data-testid="notifications-update-button"
                  isLoading={waitingOnUpdateNotification}
                >
                  UPDATE NOTIFICATION
                </Button>
              </>
            ) : (
              <Button
                type="primary"
                onClick={() => handleCreateNotification()}
                disabled={isCreateButtonDisabled()}
                data-testid="notifications-create-button"
                isLoading={waitingOnCreateNotification}
              >
                CREATE NOTIFICATION
              </Button>
            )}
          </Grid.Item>
        </Grid.Container>
        <ManageNotificationsRecords
          currentNotifications={currentNotifications}
          updateCurrentNotifications={updateCurrentNotifications}
          waitingOnGetNotifications={waitingOnGetNotifications}
          getNotifications={fetchNotifications}
          setIsUpdating={setIsUpdating}
          isUpdatingNotification={isUpdatingNotification}
          updatingNotificationId={updatingNotificationId}
        />
      </Layout.Body>
    </>
  )
}
